import { Injectable } from '@angular/core';
import { Observable, Subject, Subscription, catchError, finalize, forkJoin, merge, of, take, tap, throwError } from 'rxjs';
import { ActionPlanDrillDownStatusBadge, ActionplanAnalyticsService } from '@solvace/core/services/analytics/actionplan';
import { InspectionDrillDownStatusBadge as ChecklistInspectionDrillDownStatusBadge, ChecklistAnalyticsService } from '@solvace/core/services/analytics/checklist';
import { DefectTagDrillDownStatusBadge, DefectTagDrillDownStatusIdBadge, DefecttagAnalyticsService } from '@solvace/core/services/analytics/defecttag';
import { InspectionDrillDownStatusBadge as LilInspectionDrillDownStatusBadge, CilAnalyticsService } from '@solvace/core/services/analytics/cil';
import { AuthService } from '../../auth/auth.service';
import { StaticApplicationId } from '../../Constants/static-application-id.enum';
import { SettingsService } from '../settings/services/settings.service';
import { ModuleAvailabilityService } from '../modules/module-availability.service';
import { ModuleVariant } from '../modules/module-availability.types';
import { CommomAnalyticsIndicator } from './models/commom-analytics-indicator';
import { TasksModule } from './models/tasks-module';
import { TasksFilter } from './models/tasks-filter';
import { TasksCategory } from './models/tasks-category';
import { getCustomThemeColor } from 'projects/lib-shared-common/src/public-api';

export enum MainTasksIndicatorType {
  NONE,
  USERS,
  SUBORDINATES,
}

@Injectable({
  providedIn: 'root',
})
export class MainTasksService {
  hasFilter: boolean = false;
  _allTasks: any = {};

  private set allTasks(value: any) {
    value[TasksCategory.LATE] = this.#sortTasks(value[TasksCategory.LATE], false);
    value[TasksCategory.TODAY] = this.#sortTasks(value[TasksCategory.TODAY], false);
    value[TasksCategory.TOMORROW_UP_TO_7_DAYS] = this.#sortTasks(value[TasksCategory.TOMORROW_UP_TO_7_DAYS], true);
    value[TasksCategory.MORE_THAN_7_DAYS] = this.#sortTasks(value[TasksCategory.MORE_THAN_7_DAYS], true);

    this._allTasks = value;
  }

  public get allTasks() {
    return this._allTasks;
  }

  public indicatorsLoadingType = MainTasksIndicatorType.NONE;
  private _allTasksUpdated = new Subject<any>();
  allTasksUpdated$ = this._allTasksUpdated.asObservable();

  private _isAllTasksLoading = new Subject<any>();
  isAllTasksLoading$ = this._isAllTasksLoading.asObservable();

  private _hasError = new Subject<{ hasError: boolean; apiName?: string }>();
  hasError$ = this._hasError.asObservable();

  private filter: TasksFilter;

  private _taskFiltering = new Subject<TasksFilter>();
  taskFiltering$ = this._taskFiltering.asObservable();

  private _bookmarkUpdated = new Subject<{ moduleName: TasksModule; uniqueId: number; isBookmark: boolean }>();
  bookmarkUpdated$ = this._bookmarkUpdated.asObservable();

  #subscriptions: Subscription[] = [];

  constructor(
    private _settingsService: SettingsService,
    private _authService: AuthService,
    private _checklistAnalyticsService: ChecklistAnalyticsService,
    private _actionPlanAnalyticsService: ActionplanAnalyticsService,
    private _defectTagAnalyticsService: DefecttagAnalyticsService,
    private _cilAnalyticsService: CilAnalyticsService,
    private _moduleAvailability: ModuleAvailabilityService
  ) {
    merge(this._authService.signIn$, this._authService.signOut$)
      .pipe(tap(() => this.#resetData()))
      .subscribe();
  }

  #sortTasks(tasks: any[], ascending: boolean): any[] {
    const moduleOrder = [TasksModule.ACTIONPLAN, TasksModule.DEFECTTAG, TasksModule.CIL, TasksModule.CHECKLIST];
    return tasks?.sort((a, b) => (ascending ? a.deadline - b.deadline : b.deadline - a.deadline) || moduleOrder.indexOf(a.moduleName) - moduleOrder.indexOf(b.moduleName)) || [];
  }

  getFilterModule(): TasksModule {
    return this.filter?.module;
  }

  filterTasks(filter: TasksFilter) {
    this.filter = filter;
    this.hasFilter = !!filter?.module;
    const updateOnly =
      filter?.module != null && filter?.module != undefined
        ? {
            actionPlan: filter?.module == TasksModule.ACTIONPLAN,
            checklist: filter?.module == TasksModule.CHECKLIST,
            lil: filter?.module == TasksModule.CIL,
            defectTag: filter?.module == TasksModule.DEFECTTAG,
            kaizen: false,
          }
        : null;
    if (filter?.view === 'Subordinates') {
      this.getSubordinatesDrillDowns(updateOnly);
    } else {
      this.getUserDrillDowns(updateOnly);
    }

    this._taskFiltering.next(this.filter);
  }

  #getTasksByTypeAndModules(type: TasksCategory, onlyModules: TasksModule[], isGetAll: boolean): any[] {
    return isGetAll ? [] : (this._allTasks[type]?.filter((item) => !onlyModules.includes(item.moduleName)) ?? []);
  }

  #getOnlyModules(only: { checklist?: boolean; lil?: boolean; actionPlan?: boolean; defectTag?: boolean }): TasksModule[] {
    const moduleMap: Record<string, TasksModule> = {
      checklist: TasksModule.CHECKLIST,
      lil: TasksModule.CIL,
      actionPlan: TasksModule.ACTIONPLAN,
      defectTag: TasksModule.DEFECTTAG,
    };
    const modules: TasksModule[] = [];

    if (only) {
      Object.keys(only || {}).forEach((key) => {
        if (only?.[key]) modules.push(moduleMap?.[key]);
      });
    }
    return modules;
  }

  #setAllTasks(onlyModules: TasksModule[], isGetAll: boolean): void {
    if (isGetAll) this._allTasks = {};
    const categories = [TasksCategory.LATE, TasksCategory.TODAY, TasksCategory.TOMORROW_UP_TO_7_DAYS, TasksCategory.MORE_THAN_7_DAYS];

    categories.forEach((category) => (this.allTasks[category] = this.#getTasksByTypeAndModules(category, onlyModules, isGetAll)));

    this._allTasks = {
      ...this._allTasks,
      count: Object.fromEntries(categories.map((category) => [category, 0])),
      loaded: Object.fromEntries(categories.map((category) => [category, false])),
    };
  }

  #getOnlyData(only: { checklist?: boolean; lil?: boolean; actionPlan?: boolean; defectTag?: boolean }) {
    if (only && Object.keys(only).some((key) => only[key])) {
      only = {
        checklist: only.checklist ?? false,
        lil: only.lil ?? false,
        actionPlan: only.actionPlan ?? false,
        defectTag: only.defectTag ?? false,
      };
    }

    return only;
  }

  getUserDrillDowns(only?: { checklist?: boolean; lil?: boolean; actionPlan?: boolean; defectTag?: boolean }, type = TasksCategory.TODAY) {
    this.#cancelSubscriptions();
    this.indicatorsLoadingType = MainTasksIndicatorType.USERS;
    only = this.#getOnlyData(only);
    const onlyModules: TasksModule[] = this.#getOnlyModules(only);
    this.#setAllTasks(onlyModules, !onlyModules.length);

    this._allTasksUpdated.next(structuredClone(this.allTasks));

    this._isAllTasksLoading.next(true);

    const services = [
      {
        key: 'actionPlan',
        service: this._actionPlanAnalyticsService.getActionPlanCategorizedDrillDown.bind(this._actionPlanAnalyticsService),
        addFn: this.addActionPlanTasks.bind(this),
      },
      {
        key: 'checklist',
        service: this._checklistAnalyticsService.getInspectionCategorizedDrillDown.bind(this._checklistAnalyticsService),
        addFn: this.addChecklistTasks.bind(this),
      },
      { key: 'lil', service: this._cilAnalyticsService.getInspectionCategorizedDrillDown.bind(this._cilAnalyticsService), addFn: this.addLilTasks.bind(this) },
      {
        key: 'defectTag',
        service: this._defectTagAnalyticsService.getDefectTagCategorizedDrillDown.bind(this._defectTagAnalyticsService),
        addFn: this.addDefectTagTasks.bind(this),
      },
    ];

    // AB#73024 Action Plan / Defect Tag bookmark by actionPlanUniqueId/defectTagUniqueId
    // on the REVAMP variant, but the legacy variant never gets a real unique id from
    // analytics (always 0) — legacy bookmarks by actionPlanId/defectTagId instead, and
    // the applicationId must match whichever variant is actually active.
    this.#resolveModuleVariants()
      .pipe(take(1))
      .subscribe((variants) => {
        const requests: Subscription[] = [];

        services.forEach(({ key, service, addFn }) => {
          if (only?.[key] ?? true) {
            requests.push(
              service(type)
                .pipe(
                  tap((drillDown: any[]) => {
                    drillDown.forEach((ddlItem: any) => (this.allTasks = addFn(ddlItem, variants[key])));
                    this._allTasksUpdated.next(structuredClone(this.allTasks));
                  }),
                  catchError((error) => {
                    this.handleError(error, key);
                    return throwError(() => error);
                  }),
                  finalize(() => this.#checkIfAllDone(requests, type))
                )
                .subscribe()
            );
          }
        });
        this.#addSubscriptions(requests);
      });
  }

  /**
   * Resolves the Action Plan / Defect Tag legacy-vs-revamp variant for the current
   * tenant, so the drill-down mapping knows which id/applicationId to bookmark by.
   * Falls back to "legacy" on lookup failure, matching `ActionPlanTaskOpenerService`.
   */
  #resolveModuleVariants(): Observable<{ actionPlan: ModuleVariant; defectTag: ModuleVariant }> {
    const actionPlanFallback: ModuleVariant = { isLegacy: true, isRevamp: false, activeApplicationId: StaticApplicationId.actionPlan };
    const defectTagFallback: ModuleVariant = { isLegacy: true, isRevamp: false, activeApplicationId: StaticApplicationId.defectTagLegacy };
    return forkJoin({
      actionPlan: this._moduleAvailability.actionPlanVariant$.pipe(take(1), catchError(() => of(actionPlanFallback))),
      defectTag: this._moduleAvailability.defectTagVariant$.pipe(take(1), catchError(() => of(defectTagFallback))),
    });
  }

  public categoryNeedsLoad(category: TasksCategory, allTasks: any = undefined): boolean {
    const tasks = allTasks || this.allTasks;
    return tasks.count?.[category] > 0 && !tasks?.loaded?.[category] && tasks[category]?.length === 0;
  }

  /**
   * Reflects a bookmark (favorite) toggle made outside the activity bar — e.g. in
   * the Action Plan revamp record modal — onto the matching action plan drill-down
   * task, so the bar's star updates without a full reload (AB#68784).
   */
  public syncActionPlanBookmark(actionPlanUniqueId: number, isBookmark: boolean): void {
    this.#syncBookmark(TasksModule.ACTIONPLAN, actionPlanUniqueId, isBookmark);
  }

  /**
   * Same as {@link syncActionPlanBookmark} for the Defect Tag modal — both Action
   * Plan and Defect Tag expose a favorite star in their revamp record modal, so a
   * toggle there must repaint the matching activity-bar row too (AB#68784).
   */
  public syncDefectTagBookmark(defectTagUniqueId: number, isBookmark: boolean): void {
    this.#syncBookmark(TasksModule.DEFECTTAG, defectTagUniqueId, isBookmark);
  }

  /**
   * Reflects a bookmark (favorite) toggle made outside the activity bar onto the
   * matching drill-down task of the given module, keyed by `uniqueId` (the same id
   * the revamp modal broadcasts as `referenceId`).
   *
   * The rendered tasks are `structuredClone`s emitted through `allTasksUpdated$`,
   * so mutating `_allTasks` here would not touch the objects bound in the template.
   * We update the master copy for consistency AND relay the change on
   * `bookmarkUpdated$`, which `MainTasksComponent` applies to its own rendered tasks
   * so the `svc-bookmark-button` `[initialValue]` binding repaints.
   */
  #syncBookmark(moduleName: TasksModule, uniqueId: number, isBookmark: boolean): void {
    const target = Number(uniqueId);
    if (!Number.isFinite(target)) return;

    Object.values(this._allTasks ?? {}).forEach((tasks) => {
      if (!Array.isArray(tasks)) return;
      tasks.forEach((task) => {
        if (task?.moduleName === moduleName && Number(task.uniqueId) === target) {
          task.isBookmark = isBookmark;
        }
      });
    });

    this._bookmarkUpdated.next({ moduleName, uniqueId: target, isBookmark });
  }

  #cancelSubscriptions(): void {
    this.#subscriptions?.forEach((subscription) => subscription?.unsubscribe());
    this.#subscriptions = [];
  }

  #addSubscriptions(requests: Subscription[]): void {
    requests?.forEach((request) => this.#subscriptions.push(request));
  }

  #checkIfAllDone(requests: Subscription[], type: TasksCategory): void {
    if (requests.every((request) => request.closed)) {
      this.allTasks.loaded[type] = true;
      this._isAllTasksLoading.next(false);
    }
  }

  getSubordinatesDrillDowns(only?: { checklist?: boolean; lil?: boolean; actionPlan?: boolean; defectTag?: boolean }, type = TasksCategory.TODAY) {
    this.#cancelSubscriptions();
    this.indicatorsLoadingType = MainTasksIndicatorType.SUBORDINATES;
    only = this.#getOnlyData(only);
    const onlyModules: TasksModule[] = this.#getOnlyModules(only);
    this.#setAllTasks(onlyModules, !onlyModules.length);

    this._allTasksUpdated.next(structuredClone(this.allTasks));

    this._isAllTasksLoading.next(true);

    const services = [
      {
        key: 'actionPlan',
        service: this._actionPlanAnalyticsService.getActionPlanCategorizedDrillDownSubordinates.bind(this._actionPlanAnalyticsService),
        addFn: this.addActionPlanTasks.bind(this),
      },
      {
        key: 'checklist',
        service: this._checklistAnalyticsService.getInspectionCategorizedDrillDownSubordinates.bind(this._checklistAnalyticsService),
        addFn: this.addChecklistTasks.bind(this),
      },
      { key: 'lil', service: this._cilAnalyticsService.getInspectionCategorizedDrillDownSubordinates.bind(this._cilAnalyticsService), addFn: this.addLilTasks.bind(this) },
      {
        key: 'defectTag',
        service: this._defectTagAnalyticsService.getDefectTagCategorizedDrillDownSubordinates.bind(this._defectTagAnalyticsService),
        addFn: this.addDefectTagTasks.bind(this),
      },
    ];

    // AB#73024 see getUserDrillDowns — same legacy/revamp bookmark key.
    this.#resolveModuleVariants()
      .pipe(take(1))
      .subscribe((variants) => {
        const requests: Subscription[] = [];

        services.forEach(({ key, service, addFn }) => {
          if (only?.[key] ?? true) {
            requests.push(
              service(type)
                .pipe(
                  tap((drillDown: any[]) => {
                    drillDown.forEach((ddlItem: any) => (this.allTasks = addFn(ddlItem, variants[key])));
                    this._allTasksUpdated.next(structuredClone(this.allTasks));
                  }),
                  catchError((error) => {
                    this.handleError(error, key);
                    return throwError(() => error);
                  }),
                  finalize(() => this.#checkIfAllDone(requests, type))
                )
                .subscribe()
            );
          }
        });
        this.#addSubscriptions(requests);
      });
  }

  private handleError(error: any, source: string) {
    this._hasError.next({ hasError: true, apiName: source });
    return throwError(() => error);
  }

  private addChecklistTasks(ddlItem: any) {
    return {
      ...this.allTasks,
      [ddlItem.indicatorEnum]: [
        ...(this.allTasks[ddlItem.indicatorEnum] || []),
        ...ddlItem.indicatorItems.map((item) => ({
          id: item.checkListId,
          date: item.checkListCreateDate,
          label: 'CHK',
          inspectionId: item.inspectionId,
          registryNumber: item.inspectionRegistryNumber,
          description: item.checkListName,
          moduleName: TasksModule.CHECKLIST,
          status: ChecklistInspectionDrillDownStatusBadge(item.checkListStatus),
          locked: item.isPrivate,
          owners: item.owners?.map((owner) => ({
            id: owner.ownerId,
            userName: owner.ownerName,
            picture: owner.userPictureFile,
            ownerType: owner.ownerType ?? 1,
            initialsColor: owner.initialColor ? owner.initialColor : getCustomThemeColor(this._settingsService.theme$, 500),
          })),
          deadline: item.deadline ?? item.age,
          itemsData: {
            total: item.inspectionItemsNotOk + item.inspectionItemsOk + item.inspectionItemsPending,
            totalPerformed: item.inspectionItemsOk + item.inspectionItemsNotOk,
            data: [
              { label: 'OK', value: item.inspectionItemsOk, color: '#466900' },
              { label: 'NOK', value: item.inspectionItemsNotOk, color: '#f44336', customClass: 'cursor-pointer' },
              { label: 'PENDENTE', value: item.inspectionItemsPending, color: '#d4d4d4' },
            ],
          },
        })),
      ],
      count: {
        ...(this._allTasks?.count || {}),
        [ddlItem.indicatorEnum]: this.#getCountTasks(ddlItem.indicatorEnum, ddlItem?.indicatorItemsCounter),
      },
    };
  }

  private addActionPlanTasks(ddlItem: any, variant?: ModuleVariant) {
    return {
      ...this.allTasks,
      [ddlItem.indicatorEnum]: [
        ...(this.allTasks[ddlItem.indicatorEnum] || []),
        ...ddlItem.indicatorItems.map(
          (item) =>
            ({
              id: item.actionPlanId,
              // AB#73024 revamp bookmarks by actionPlanUniqueId; legacy has no real
              // unique id (analytics always reports 0), so it bookmarks by actionPlanId.
              uniqueId: variant?.isRevamp ? item.actionPlanUniqueId : item.actionPlanId,
              actionPlanUniqueId: item.actionPlanUniqueId,
              date: item.createDate,
              label: 'PLA',
              description: item.actionPlanName,
              moduleName: TasksModule.ACTIONPLAN,
              status: ActionPlanDrillDownStatusBadge(item.status),
              hasBookmark: true,
              isBookmark: undefined,
              applicationId: variant?.activeApplicationId ?? StaticApplicationId.actionPlanRevamp,
              locked: item.isPrivate,
              owners: item.owners?.map((owner) => ({
                id: owner.userId,
                userName: owner.userName,
                picture: owner.pictureFile,
                ownerType: owner.ownerType ?? 1,
                initialsColor: owner.initialColor ? owner.initialColor : getCustomThemeColor(this._settingsService.theme$, 500),
              })),
              deadline: item.deadline ?? item.age,
            }) as CommomAnalyticsIndicator
        ),
      ],
      count: {
        ...(this._allTasks?.count || {}),
        [ddlItem.indicatorEnum]: this.#getCountTasks(ddlItem.indicatorEnum, ddlItem?.indicatorItemsCounter),
      },
    };
  }

  private addLilTasks(ddlItem: any) {
    return {
      ...this.allTasks,
      [ddlItem.indicatorEnum]: [
        ...(this.allTasks[ddlItem.indicatorEnum] || []),
        ...ddlItem.indicatorItems.map((item) => ({
          id: item.cilId,
          date: item.cilCreateDate,
          label: 'LIL',
          inspectionId: item.inspectionId,
          registryNumber: item.inspectionRegistryNumber,
          description: item.cilName,
          moduleName: TasksModule.CIL,
          status: LilInspectionDrillDownStatusBadge(item.cilStatus),
          locked: item.isPrivate,
          owners: item.owners?.map((owner) => ({
            id: owner.ownerId,
            userName: owner.ownerName,
            picture: owner.userPictureFile,
            ownerType: owner.ownerType ?? 1,
            initialsColor: owner.initialColor ? owner.initialColor : getCustomThemeColor(this._settingsService.theme$, 500),
          })),
          deadline: item.deadline ?? item.age,

          itemsData: {
            total: item.inspectionItemsNotOk + item.inspectionItemsOk + item.inspectionItemsPending,
            totalPerformed: item.inspectionItemsOk + item.inspectionItemsNotOk,
            data: [
              { label: 'OK', value: item.inspectionItemsOk, color: '#466900' },
              { label: 'NOK', value: item.inspectionItemsNotOk, color: '#f44336', customClass: 'cursor-pointer' },
              { label: 'PENDENTE', value: item.inspectionItemsPending, color: '#d4d4d4' },
            ],
          },
        })),
      ],
      count: {
        ...(this._allTasks?.count || {}),
        [ddlItem.indicatorEnum]: this.#getCountTasks(ddlItem.indicatorEnum, ddlItem?.indicatorItemsCounter),
      },
    };
  }

  private addDefectTagTasks(ddlItem: any, variant?: ModuleVariant) {
    return {
      ...this.allTasks,
      [ddlItem.indicatorEnum]: [
        ...(this.allTasks[ddlItem.indicatorEnum] || []),
        ...ddlItem.indicatorItems.map(
          (item) =>
            ({
              id: item.defectTagId,
              // AB#73024 revamp bookmarks by defectTagUniqueId; legacy has no real
              // unique id (analytics always reports 0), so it bookmarks by defectTagId.
              uniqueId: variant?.isRevamp ? item.defectTagUniqueId : item.defectTagId,
              date: item.inclusionDate,
              label: 'DFT',
              hasBookmark: true,
              isBookmark: undefined,
              applicationId: variant?.activeApplicationId ?? StaticApplicationId.defectTag,
              description: item.defect,
              defectTagColor: item.defectTagColor,
              moduleName: TasksModule.DEFECTTAG,
              status: item.defectTagStatusId == null ? DefectTagDrillDownStatusBadge(item.status) : DefectTagDrillDownStatusIdBadge(item.defectTagStatusId, item.defectTagSubStatusId),
              owners: item.owners?.map((owner) => ({
                id: owner.userId,
                userName: owner.userName,
                picture: owner.pictureFile,
                ownerType: owner.ownerType ?? 1,
                initialsColor: owner.initialColor ? owner.initialColor : getCustomThemeColor(this._settingsService.theme$, 500),
              })),
              deadline: item.deadline ?? item.age,
            }) as CommomAnalyticsIndicator
        ),
      ],
      count: {
        ...(this._allTasks?.count || {}),
        [ddlItem.indicatorEnum]: this.#getCountTasks(ddlItem.indicatorEnum, ddlItem?.indicatorItemsCounter),
      },
    };
  }

  #resetData(): void {
    this.hasFilter = false;
    this._allTasks = {};
    this.indicatorsLoadingType = MainTasksIndicatorType.NONE;
    this.filter = null;
  }

  #getCountTasks(type: TasksCategory, indicatorItemsCounter: number): number {
    return (this._allTasks?.count?.[type] ?? 0) + (indicatorItemsCounter ?? 0);
  }
}
